import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { UserRestaurantsRole } from '../reducers/restaurantSlice';
import { selectActiveRestaurantCode } from '../selectors/restaurant';
import {
  RolePermissions,
  UserRestaurantGroupRoleInput,
} from '../types/restaurant';

export const restaurantAccessLevelsSelector = (state: RootState) =>
  state.restaurant.restaurantAccessLevels;

export const getSelectedRestaurantAccess = (
  selectedRestaurantCode: string | null,
  accessLevels: RestaurantAccess
): RolePermissions | null => {
  if (selectedRestaurantCode) {
    return accessLevels[selectedRestaurantCode]?.role || null;
  }
  return null;
};

export const selectedRestaurantAccessSelector = createSelector(
  selectActiveRestaurantCode,
  restaurantAccessLevelsSelector,
  getSelectedRestaurantAccess
);

export interface UserRestaurantGroupRole extends UserRestaurantGroupRoleInput {
  groupName: string;
}

const ROLE_ACCESS = {
  [RolePermissions.PrestoAdmin.toString()]: 0,
  [RolePermissions.PrestoCustomerSupport.toString()]: 1,
  [RolePermissions.RestaurantManager.toString()]: 2,
  [RolePermissions.RestaurantStaff.toString()]: 3,
  [RolePermissions.Limited.toString()]: 4,
};

export const hasRoleAccess = (
  neededRole: RolePermissions | null,
  highestAvailableRole: RolePermissions | null
) => {
  if (!neededRole) {
    return true;
  }
  if (!highestAvailableRole) {
    return false;
  }
  return ROLE_ACCESS[highestAvailableRole] <= ROLE_ACCESS[neededRole];
};

export const getHighestAccessRestaurant = (roles: UserRestaurantsRole[]) => {
  let highestRoleNumber;
  let highestRole;
  for (let role of roles) {
    if (
      highestRoleNumber === undefined ||
      ROLE_ACCESS[role.role] < highestRoleNumber
    ) {
      highestRoleNumber = ROLE_ACCESS[role.role];
      highestRole = role;
    }
  }
  if (highestRole) {
    return highestRole.restaurants.length && highestRole.restaurants[0];
  }
  return undefined;
};

export interface RestaurantAccess {
  [restaurantCode: string]: {
    role: RolePermissions;
  };
}

export const getRestaurantAccessLevels = (roles: UserRestaurantsRole[]) => {
  const accessLevels: RestaurantAccess = {};
  for (let role of roles) {
    for (let restaurant of role.restaurants) {
      if (!accessLevels[restaurant.restaurantCode]) {
        accessLevels[restaurant.restaurantCode] = { role: role.role };
      } else {
        if (
          ROLE_ACCESS[role.role] <
          ROLE_ACCESS[accessLevels[restaurant.restaurantCode].role]
        ) {
          accessLevels[restaurant.restaurantCode].role = role.role;
        }
      }
    }
  }
  return accessLevels;
};

export const getUserAccessLevel = (roles: UserRestaurantGroupRole[]) => {
  if (roles.length === 0) return RolePermissions.RestaurantManager;
  let accessLevel: RolePermissions = roles[0].permission;
  roles.forEach((role) => {
    if (!accessLevel) {
      accessLevel = role.permission;
    } else {
      accessLevel =
        ROLE_ACCESS[role.permission] > ROLE_ACCESS[accessLevel]
          ? accessLevel
          : role.permission;
    }
  });

  return accessLevel;
};

/**
 * True when the restaurant is on the consolidated (brand) menu, so the
 * per-restaurant `unified_menu` Unleash lookup can be skipped on the task-load
 * critical path — UNLESS its full code is in `excludedRestaurants` (a
 * not-yet-migrated store that still needs the live check). `brands` &
 * `excludedRestaurants` come from the config slice (env-driven). A brand
 * matches when its name appears anywhere in the restaurant code (substring).
 */
export const isConsolidatedMenuRestaurant = (
  restaurantCode: string,
  brands: string[],
  excludedRestaurants: string[]
): boolean => {
  const lower = restaurantCode.toLowerCase();
  if (excludedRestaurants.some((code) => code.toLowerCase() === lower)) {
    return false;
  }
  return brands.some((brand) => lower.includes(brand.toLowerCase()));
};

/**
 * Resolves the consolidated-menu **brand key** for a restaurant code, used to
 * cache the brand menu once per brand (shared across all of a brand's stores)
 * instead of once per restaurant. Returns the first brand token — in
 * `CONSOLIDATED_MENU_BRANDS` order — that appears in the code, so a given
 * restaurant always maps to the same key. Returns `null` when the restaurant is
 * not on the consolidated menu (no brand matches) or is explicitly excluded,
 * in which case the caller falls back to the per-restaurant fetch/cache path.
 *
 * Note: alias tokens for the same brand (e.g. `dq` / `dairy_queen`) resolve to
 * whichever appears first in the list, so alias groups may occupy separate
 * cache slots — harmless (each is version-guarded), just one missed dedup.
 */
export const getConsolidatedMenuBrand = (
  restaurantCode: string,
  brands: string[],
  excludedRestaurants: string[]
): string | null => {
  const lower = restaurantCode.toLowerCase();
  if (excludedRestaurants.some((code) => code.toLowerCase() === lower)) {
    return null;
  }
  const matched = brands.find((brand) => lower.includes(brand.toLowerCase()));
  return matched ? matched.toLowerCase() : null;
};
